<template>
  <div>
    <div class="flex items-center justify-between mb-2">
      <label class="text-sm font-medium text-gray-800">Author Voice</label>
      <div class="flex items-center gap-2">
        <button
          v-if="store.authorOverriddenFields.value.length > 0"
          @click="store.clearAuthorOverrides()"
          class="text-sm text-orange-600 hover:text-orange-800"
        >Reset all</button>
        <button
          v-if="store.authorOverriddenFields.value.length > 0 && store.currentAuthor.value"
          @click="store.saveBackToAuthor()"
          class="text-sm bg-orange-500 text-white rounded px-2 py-0.5 hover:bg-orange-600"
        >Save to Author</button>
      </div>
    </div>

    <p v-if="!store.currentAuthor.value" class="text-sm text-gray-600 italic mb-2">
      Select an author to load their voice settings, or type overrides below.
    </p>

    <div class="space-y-3">
      <div v-for="field in voiceFields" :key="field.key">
        <label class="text-sm font-medium text-gray-800 mb-0.5 flex items-center gap-1">
          {{ field.label }}
          <span v-if="isOverridden(field.key)" class="w-1.5 h-1.5 bg-orange-400 rounded-full" title="Overridden"></span>
        </label>
        <p v-if="field.hint" class="text-sm text-gray-600 mb-0.5">{{ field.hint }}</p>
        <textarea
          v-if="field.multiline"
          ref="autoTextareas"
          :value="effective(field.key)"
          @input="onUpdate(field.key, $event.target.value); autoResize($event.target)"
          :placeholder="field.placeholder"
          rows="1"
          class="w-full text-sm text-gray-900 border rounded px-2 py-1.5 focus:outline-none resize-none overflow-hidden"
          :class="isOverridden(field.key)
            ? 'border-orange-300 bg-orange-50/50 focus:border-orange-400'
            : 'border-gray-300 focus:border-teal-500'"
        ></textarea>
        <input
          v-else
          type="text"
          :value="effective(field.key)"
          @input="onUpdate(field.key, $event.target.value)"
          :placeholder="field.placeholder"
          class="w-full text-sm text-gray-900 border rounded px-2 py-1.5 focus:outline-none"
          :class="isOverridden(field.key)
            ? 'border-orange-300 bg-orange-50/50 focus:border-orange-400'
            : 'border-gray-300 focus:border-teal-500'"
        />
      </div>
    </div>

    <!-- Writing Rules (read-only display) -->
    <div class="mt-3">
      <label class="text-sm font-medium text-gray-800 mb-1 block">Writing Rules</label>
      <div v-if="currentWritingRules" class="text-sm text-gray-800 bg-white rounded border border-gray-200 p-2 whitespace-pre-line">{{ currentWritingRules }}</div>
      <div v-else class="text-sm text-gray-600 italic">None set</div>
    </div>

    <!-- Quirks (read-only display) -->
    <div class="mt-2">
      <label class="text-sm font-medium text-gray-800 mb-1 block">Quirks</label>
      <div v-if="currentQuirks.length" class="text-sm text-gray-800 bg-white rounded border border-gray-200 p-2">
        <ul class="ml-3 list-disc">
          <li v-for="(q, i) in currentQuirks" :key="'q-'+i">{{ q }}</li>
        </ul>
      </div>
      <div v-else class="text-sm text-gray-600 italic">None set</div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, ref, watch, nextTick, onMounted } from 'vue'

const store = inject('workbench')

const autoTextareas = ref([])

const voiceFields = [
  {
    key: 'voice_description', label: 'Voice description', multiline: true,
    placeholder: 'Warm and knowledgeable, like a senior colleague explaining something over coffee. Uses real-world analogies to make abstract concepts click. Confident but never preachy.',
    hint: 'The main voice brief. 2-5 sentences describing how this author writes.',
  },
  {
    key: 'perspective', label: 'Perspective', multiline: false,
    placeholder: 'First person plural (we/our)',
    hint: 'Point of view. 1-3 words.',
  },
  {
    key: 'formality', label: 'Formality', multiline: true,
    placeholder: 'Professional but relaxed \u2014 like a well-prepared meeting, not a legal brief. Contractions are fine. No slang.',
    hint: 'Tone register. 1-2 sentences.',
  },
  {
    key: 'sentence_style', label: 'Sentence style', multiline: true,
    placeholder: 'Mix of short and long. Short sentences for emphasis. Longer ones to build an argument. Never more than two commas per sentence.',
    hint: 'How sentences are structured. 1-2 sentences.',
  },
  {
    key: 'personality', label: 'Personality', multiline: true,
    placeholder: 'Curious and slightly irreverent. Genuinely excited about the topic. Cracks the occasional dry joke but never at the reader\u2019s expense.',
    hint: 'Character traits that come through in writing. 1-2 sentences.',
  },
  {
    key: 'jargon_approach', label: 'Jargon approach', multiline: true,
    placeholder: 'Uses industry terms but always defines them on first use in parentheses. After that, uses them freely.',
    hint: 'How to handle technical terms. 1 sentence.',
  },
]

function effective(key) {
  return store.effectiveAuthor.value?.[key] || ''
}

function isOverridden(key) {
  return store.authorOverriddenFields.value.includes(key)
}

function onUpdate(key, value) {
  store.setAuthorOverride(key, value)
}

// --- Auto-resize textareas ---
function autoResize(el) {
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

function resizeAllTextareas() {
  nextTick(() => {
    const els = autoTextareas.value || []
    for (const el of els) {
      if (el) autoResize(el)
    }
  })
}

onMounted(resizeAllTextareas)

// Resize when author changes (new values loaded)
watch(() => store.effectiveAuthor.value, resizeAllTextareas, { deep: true })

const currentWritingRules = computed(() => store.currentAuthor.value?.writing_rules || '')
const currentQuirks = computed(() => store.currentAuthor.value?.quirks || [])
</script>
